import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { ModalService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ProductionCardStatus } from '@app/models/production/production-card';
import {
  ProductionCardComponentSummaryDto,
  ProductionCardSummaryDto,
  ProductionDashboardSearchCommand,
  ProductionDashboardWorkOrderDto,
  ProductionTreeRow,
  UomAndQuantityKeyDto,
  WorkOrderStatus,
  WorkOrderTerm,
  WorkOrderType,
} from '@app/models/production/production-dashboard';
import { ProductionDashboardProxyService } from '@app/proxies/production/production-dashboard.proxy.service';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { WorkOrderQuantitiesModalComponent } from '../../shared/_modals/work-order-quantities-modal/work-order-quantities-modal.component';

@Component({
  selector: 'app-dashboard-production-card',
  templateUrl: './dashboard-production-card.component.html',
  styleUrl: './dashboard-production-card.component.scss',
})
export class DashboardProductionCardComponent implements OnDestroy {
  private readonly dashboardProxy = inject(ProductionDashboardProxyService);
  private readonly reportProxyService = inject(ReportProxyService);
  private readonly modalService = inject(ModalService);
  private readonly localeHelper = inject(LocaleHelperService);

  workOrders = signal<ProductionDashboardWorkOrderDto[]>([]);
  isLoading = signal<boolean>(false);
  totalCount = signal<number>(0);
  currentPage = signal<number>(1);
  pageSize = signal<number>(10);
  firstRecord = signal<number>(0);

  // Filter controls
  filterWorkOrderNumber = new FormControl<string | null>(null);
  filterProductionCardNumber = new FormControl<string | null>(null);
  filterProductionCardStatus = new FormControl<ProductionCardStatus[] | null>(null);
  filterWorkOrderStatus = new FormControl<WorkOrderStatus[] | null>(null);

  // Production card status options for filter dropdown
  productionCardStatusOptions: { label: string; value: ProductionCardStatus }[] = Object.entries(ProductionCardStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([label, value]) => ({ label, value: value as ProductionCardStatus }));

  // Work order status options for filter dropdown
  workOrderStatusOptions: { label: string; value: WorkOrderStatus }[] = Object.entries(WorkOrderStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([label, value]) => ({ label, value: value as WorkOrderStatus }));

  columns = signal([
    { field: 'toggler', header: '', headerFallback: '', width: '50px' },
    {
      field: 'orderNumber',
      header: 'dashboardProductionCards.columnOrderCardFabric',
      headerFallback: 'Order / Card / Fabric',
      width: '200px',
    },
    { field: 'status', header: 'dashboardProductionCards.columnStatus', headerFallback: 'Status', width: '120px' },
    { field: 'clientInfo', header: 'dashboardProductionCards.columnClientColor', headerFallback: 'Client / Color', width: '150px' },
    { field: 'brandInfo', header: 'dashboardProductionCards.columnBrandLabDip', headerFallback: 'Brand / Lab Dip', width: '150px' },
    {
      field: 'requiredQty',
      header: 'dashboardProductionCards.columnRequiredQty',
      headerFallback: 'Required Qty',
      width: '100px',
      align: 'right',
    },
    { field: 'knittedQty', header: 'dashboardProductionCards.columnKnitted', headerFallback: 'Knitted', width: '100px', align: 'right' },
    { field: 'batchedQty', header: 'dashboardProductionCards.columnBatched', headerFallback: 'Batched', width: '100px', align: 'right' },
    { field: 'dyedQty', header: 'dashboardProductionCards.columnDyed', headerFallback: 'Dyed', width: '100px', align: 'right' },
    { field: 'weighedQty', header: 'dashboardProductionCards.columnWeighed', headerFallback: 'Weighed', width: '100px', align: 'right' },
    {
      field: 'finishedGoodsQty',
      header: 'dashboardProductionCards.columnFinishedGoods',
      headerFallback: 'Finished Goods',
      width: '100px',
      align: 'right',
    },
    {
      field: 'dispatchInfo',
      header: 'dashboardProductionCards.columnDispatchInfo',
      headerFallback: 'Dispatch Info',
      width: '120px',
      align: 'right',
    },
  ]);

  treeData = computed<TreeNode<ProductionTreeRow>[]>(() => {
    const workOrders = this.workOrders();
    return workOrders.map((wo) => this.mapWorkOrderToTreeNode(wo));
  });

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    this.isLoading.set(true);

    const command: ProductionDashboardSearchCommand = {};

    if (this.filterWorkOrderNumber.value?.trim()) {
      command.workOrderNumber = this.filterWorkOrderNumber.value.trim();
    }
    if (this.filterProductionCardNumber.value?.trim()) {
      command.productionCardNumber = this.filterProductionCardNumber.value.trim();
    }
    if (this.filterProductionCardStatus.value?.length) {
      command.productionCardStatuses = this.filterProductionCardStatus.value;
    }
    if (this.filterWorkOrderStatus.value?.length) {
      command.workOrderStatuses = this.filterWorkOrderStatus.value;
    }

    this.dashboardProxy
      .search(command, this.currentPage(), this.pageSize())
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.workOrders.set(response.data.items);
            this.totalCount.set(response.data.totalCount);
          }
        },
      });
  }

  private mapWorkOrderToTreeNode(wo: ProductionDashboardWorkOrderDto): TreeNode<ProductionTreeRow> {
    const rollQuantities = wo.totalCurrentRollQuantities;

    return {
      data: {
        type: 'WORK_ORDER',
        id: wo.id,
        workOrderNumber: wo.workOrderNumber,
        workOrderType: wo.workOrderType,
        workOrderTerm: wo.workOrderTerm,
        status: wo.status,
        clientProductOrderNumbers: wo.clientProductOrderNumbers,
        clientCode: wo.clientCode,
        clientName: wo.clientName,
        brandCode: wo.brandCode,
        brandName: wo.brandName,
        requiredQuantityDisplay: this.formatQuantity(wo.requiredQuantity),
        knittedQuantityDisplay: this.formatQuantityWithRolls(rollQuantities.knittedQuantity, rollQuantities.knittedRollsCount),
        batchedQuantityDisplay: this.formatQuantityWithRolls(rollQuantities.batchedQuantity, rollQuantities.batchedRollsCount),
        dyedQuantityDisplay: this.formatQuantityWithRolls(rollQuantities.dyedQuantity, rollQuantities.dyedRollsCount),
        weighedQuantityDisplay: this.formatQuantityWithRolls(rollQuantities.weighedQuantity, rollQuantities.weighedRollsCount),
        finishedGoodsQuantityDisplay: this.formatQuantityWithRolls(
          rollQuantities.finishedGoodsQuantity,
          rollQuantities.finishedGoodsRollsCount,
        ),
        pendingDispatchQuantityDisplay: this.formatQuantityWithRolls(
          rollQuantities.pendingDispatchInstructionQuantity,
          rollQuantities.pendingDispatchInstructionRollsCount,
        ),
        dispatchedQuantityDisplay: this.formatQuantityWithRolls(rollQuantities.dispatchedQuantity, rollQuantities.dispatchedRollsCount),
        originalWorkOrder: wo,
      },
      children: wo.productionCards.map((pc) => this.mapProductionCardToTreeNode(pc, wo)),
    };
  }

  private mapProductionCardToTreeNode(pc: ProductionCardSummaryDto, wo: ProductionDashboardWorkOrderDto): TreeNode<ProductionTreeRow> {
    return {
      data: {
        type: 'PRODUCTION_CARD',
        id: wo.id,
        productionCardId: pc.productionCardId,
        productionCardNumber: pc.productionCardNumber,
        status: pc.status,
        colorReference: pc.colorReference,
        colorName: pc.colorName,
        labDipNumber: pc.labDipNumber,
        plannedQuantityDisplay: this.formatQuantity(pc.plannedQuantity),
        actualQuantityDisplay: this.formatQuantity(pc.actualQuantity),
        latestCycleStatus: pc.latestCycleStatus,
        latestCycleQCStatus: pc.latestCycleQCStatus,
      },
      children: pc.components.map((comp) => this.mapComponentToTreeNode(comp)),
    };
  }

  private mapComponentToTreeNode(comp: ProductionCardComponentSummaryDto): TreeNode<ProductionTreeRow> {
    const progressText =
      comp.completedProcessesCount !== undefined && comp.totalProcessesCount !== undefined
        ? `${comp.completedProcessesCount}/${comp.totalProcessesCount}`
        : '';

    return {
      data: {
        type: 'FABRIC',
        fabricCode: comp.fabricCode,
        fabricDescription: comp.fabricDescription,
        finishingRouteNumber: comp.finishingRouteLite?.number,
        lastProcessName: comp.lastProcessName,
        processProgress: progressText,
        plannedQuantityDisplay: this.formatQuantity(comp.plannedQuantity),
        actualQuantityDisplay: this.formatQuantity(comp.actualQuantity),
        weighedQuantityDisplay: this.formatQuantity(comp.weighedQuantity),
        weighedRollsCount: comp.weighedRollsCount,
      },
    };
  }

  private formatQuantity(quantities: UomAndQuantityKeyDto[] | undefined): string {
    if (!quantities || quantities.length === 0) return '—';
    // Show the first quantity (typically LB)
    const qty = quantities[0];
    return `${qty.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs`;
  }

  private formatQuantityWithRolls(quantities: UomAndQuantityKeyDto[] | undefined, rollsCount: number): string {
    if (!quantities || quantities.length === 0) return '—';
    const qty = quantities[0];
    const rollsLabel = this.localeHelper.translateWithFallback('dashboardProductionCards.rolls', 'rolls');
    return `${qty.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs\n${rollsCount} ${rollsLabel}`;
  }

  getStatusLabel(status: number | WorkOrderStatus | ProductionCardStatus | undefined, rowType?: string): string {
    if (status === undefined || status === null) return 'Unknown';
    const statusNumber = typeof status === 'string' ? parseInt(status, 10) : status;
    if (rowType === 'WORK_ORDER') {
      return WorkOrderStatus[statusNumber] ?? `Status ${statusNumber}`;
    }
    return ProductionCardStatus[statusNumber] ?? WorkOrderStatus[statusNumber] ?? `Status ${statusNumber}`;
  }

  getStatusClass(status: number | WorkOrderStatus | ProductionCardStatus | undefined, rowType?: string): string {
    if (status === undefined || status === null) return 'unknown';
    const statusNumber = typeof status === 'string' ? parseInt(status, 10) : status;
    const statusName =
      rowType === 'WORK_ORDER'
        ? (WorkOrderStatus[statusNumber] ?? 'Unknown')
        : (ProductionCardStatus[statusNumber] ?? WorkOrderStatus[statusNumber] ?? 'Unknown');
    return statusName.toString().toLowerCase().replace(/\s+/g, '-');
  }

  getWorkOrderTypeLabel(orderType: WorkOrderType | undefined): string {
    if (orderType === undefined || orderType === null) return '';
    return WorkOrderType[orderType] || `Type ${orderType}`;
  }

  getWorkOrderTermLabel(term: WorkOrderTerm | undefined): string {
    if (term === undefined || term === null) return '';
    const termKeys: Record<number, [string, string]> = {
      0: ['dashboardProductionCards.termCommissionKnitting', 'Commission Knitting'],
      1: ['dashboardProductionCards.termFullKnitting', 'Full Knitting'],
      2: ['dashboardProductionCards.termDyeing', 'Dyeing'],
      3: ['dashboardProductionCards.termFullPackage', 'Full Package'],
      4: ['dashboardProductionCards.termCommissionKnittingDyeing', 'Commission Knitting & Dyeing'],
    };
    const entry = termKeys[term];
    return entry ? this.localeHelper.translateWithFallback(entry[0], entry[1]) : `Term ${term}`;
  }

  clearFilters(): void {
    this.filterWorkOrderNumber.reset();
    this.filterProductionCardNumber.reset();
    this.filterProductionCardStatus.reset();
    this.filterWorkOrderStatus.reset();
  }

  applyFilters(): void {
    this.currentPage.set(1);
    this.firstRecord.set(0);
    this.loadData();
  }

  onPageChange(event: { first: number; rows: number }): void {
    const newPage = Math.floor(event.first / event.rows) + 1;
    this.currentPage.set(newPage);
    this.firstRecord.set(event.first);
    this.pageSize.set(event.rows);
    this.loadData();
  }

  printProductionCardReport(pcId: string, pcNumber: string): void {
    this.isLoading.set(true);
    this.reportProxyService
      .getProductionCardReport(pcId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          const blob = new Blob([response], { type: 'application/pdf' });
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `ProductionCard-${pcNumber}.pdf`;
          link.click();
          window.URL.revokeObjectURL(downloadUrl);
          GlobalMessageUtil.success(
            this.localeHelper.translateWithFallback('Success', 'Success'),
            this.localeHelper.translateWithFallback(
              'dashboardProductionCards.reportDownloaded',
              'Production Card report downloaded successfully.',
            ),
          );
          this.isLoading.set(false);
        },
      });
  }

  openWorkOrderQuantitiesModal(workOrder: ProductionDashboardWorkOrderDto): void {
    this.modalService.open(WorkOrderQuantitiesModalComponent, { modalClass: 'modal_large' }, { workOrder }).subscribe();
  }
}
